import {
  ColorPicker,
  Map,
  MapButton,
  Popup,
  Source,
} from '@/components/controls';
import { useInterval } from '@/hooks';
import { parseKeyValues } from '@/utils';
import { types } from '@/utils/constants';
import { FormatPaint as FormatPaintIcon } from '@mui/icons-material';
import { Box, Stack, useTheme } from '@mui/material';
import { bbox } from '@turf/turf';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { EventReplayControls } from './EventReplayControls';
import { FitMapButton } from './FitMapButton';
import { MapAutoZoom } from './MapAutoZoom';
import { Sidebar } from './Sidebar';
import { useEventReplay } from './useEventReplay';

const FRAMES_PER_SECOND_OPTIONS = [1, 2, 5, 10, 15, 30, 60];

export function EventReplay() {
  const theme = useTheme();
  const { id, type } = useParams();
  const {
    query: {
      data: { frames, paths },
      isFetching,
      isFetched,
    },
  } = useEventReplay(type, id);
  const [isPlaying, setIsPlaying] = useState(false);
  const [mapHoverInfo, setMapHoverInfo] = useState(null);
  const [listHoverInfo, setListHoverInfo] = useState(null);
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const [framesPerSecond, setFramesPerSecond] = useState(
    FRAMES_PER_SECOND_OPTIONS[0],
  );
  const [color, setColor] = useState(theme.palette.primary.main);
  const [paintAnchorEl, setPaintAnchorEl] = useState(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const boundingBox = paths
    ? Object.values(paths).reduce((acc, featureCollection) => {
        const featureCollectionBbox = bbox(featureCollection);
        if (!acc) {
          return featureCollectionBbox;
        }
        return [
          Math.min(acc[0], featureCollectionBbox[0]),
          Math.min(acc[1], featureCollectionBbox[1]),
          Math.max(acc[2], featureCollectionBbox[2]),
          Math.max(acc[3], featureCollectionBbox[3]),
        ];
      }, null)
    : null;

  useInterval(
    () => {
      if (currentFrameIndex === frames.length - 1) {
        setIsPlaying(false);
      } else {
        setCurrentFrameIndex((currentFrameIndex) => currentFrameIndex + 1);
      }
    },
    isPlaying && !isFetching ? 1000 / framesPerSecond : null,
  );

  const handleMouseMove = useCallback((event) => {
    const hoveredFeature =
      event.features && event.features[0]
        ? {
            ...event.features[0],
            properties: parseKeyValues(event.features[0].properties),
          }
        : null;

    setMapHoverInfo({
      feature: hoveredFeature,
      longitude: event.lngLat.lng,
      latitude: event.lngLat.lat,
    });
  }, []);

  function handlePlayPauseClick() {
    if (currentFrameIndex === frames.length - 1) {
      setCurrentFrameIndex(0);
    }
    setIsPlaying((isPlaying) => !isPlaying);
  }

  function handleListItemHover(hoverInfo) {
    setListHoverInfo(hoverInfo);
  }

  function handlePaintClick(event) {
    setPaintAnchorEl(event.currentTarget);
  }

  function handleSliderChange(event, value) {
    setCurrentFrameIndex(value);
  }

  function handleSkipNextClick() {
    setCurrentFrameIndex((currentFrameIndex) =>
      Math.min(currentFrameIndex + 1, frames.length - 1),
    );
  }

  function handleSkipPreviousClick() {
    setCurrentFrameIndex((currentFrameIndex) =>
      Math.max(currentFrameIndex - 1, 0),
    );
  }

  function handleFramesPerSecondClick() {
    const currentIndex = FRAMES_PER_SECOND_OPTIONS.indexOf(framesPerSecond);
    const nextIndex = (currentIndex + 1) % FRAMES_PER_SECOND_OPTIONS.length;

    setFramesPerSecond(FRAMES_PER_SECOND_OPTIONS[nextIndex]);
  }

  function onMapLoad() {
    setIsMapLoaded(true);
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <title>IR3 | Event Replay</title>
      <Box
        sx={{
          width: 320,
          height: 'calc(100vh - 48px)',
          borderRight: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Sidebar
          featureCollections={
            frames[currentFrameIndex]?.featureCollections ?? {}
          }
          hoveredFeature={mapHoverInfo?.feature}
          onHover={handleListItemHover}
        />
      </Box>
      <Map
        sx={{ flexGrow: 1, height: 'calc(100vh - 48px)' }}
        interactiveLayerIds={Object.keys(
          frames[currentFrameIndex]?.featureCollections ?? {},
        ).map((key) => `${key}-symbol-layer`)}
        onMouseMove={handleMouseMove}
        onLoad={onMapLoad}
      >
        <MapAutoZoom
          boundingBox={boundingBox}
          isReady={isFetched && isMapLoaded}
        />
        <EventReplayControls
          isPlaying={isPlaying}
          totalFrames={frames?.length ?? 0}
          framesPerSecond={framesPerSecond}
          frame={currentFrameIndex}
          frameTime={frames[currentFrameIndex]?.time}
          onPlayPauseClick={handlePlayPauseClick}
          onSkipNextClick={handleSkipNextClick}
          onSkipPreviousClick={handleSkipPreviousClick}
          onSliderChange={handleSliderChange}
          onFramesPerSecondClick={handleFramesPerSecondClick}
        />
        <Stack spacing={1} sx={{ position: 'absolute', top: 200, left: 8 }}>
          <FitMapButton boundingBox={boundingBox} />
          <MapButton title="Set feature colour" onClick={handlePaintClick}>
            <FormatPaintIcon sx={{ color }} />
          </MapButton>
          <ColorPicker
            value={color}
            anchorEl={paintAnchorEl}
            onClose={() => setPaintAnchorEl(null)}
            onChange={(value) => setColor(value)}
          />
        </Stack>
        {Object.entries(paths ?? {}).map(([type, featureCollection]) => (
          <Source
            key={type}
            id="paths"
            type={type}
            data={featureCollection}
            color={color}
          />
        ))}
        {Object.entries(frames[currentFrameIndex]?.featureCollections ?? {})
          .sort(([id1], [id2]) => types[id1].zIndex - types[id2].zIndex)
          .map(([id, data]) => (
            <Source key={id} id={id} data={data} />
          ))}
        {mapHoverInfo?.feature && <Popup hoverInfo={mapHoverInfo} />}
        {listHoverInfo && <Popup hoverInfo={listHoverInfo} />}
      </Map>
    </Box>
  );
}
